import React, { createContext, useEffect, useState } from 'react';
import PageHeaderLayout from '../../../components/Layout/PageHeaderLayout';
import { Autocomplete, Box, Button, Card, Chip, FormControl, FormControlLabel, FormLabel, Grid, IconButton, MenuItem, Modal, Radio, RadioGroup, Select, Stack, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { useForm ,FormProvider} from 'react-hook-form';
import dayjs from 'dayjs';
import { BannerServices } from './BannerServices';
import Swal from 'sweetalert2';
import calculateAspectRatio from '../../../utils/calculateRatio';
import Loading from '../../../components/Loader/Loading';
import ImageSection from './ImageSection';
import BannerForm from './BannerForm';


const BannerDataContext =  createContext();
const AddEditBanner = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const initialState = {
        title: '',
        imageUrl:'',
        platformName:'',// Customer-App,SE-App,Customer-Web,SE-Web
        bannerCategory:'',// Home,Category,Product,Profile
        bannerPosition:'',// Top,Middle,Bottom
        width: '',// in px
        height: '',// in px
        aspectRatio: '',// 16:9,1:1
        bannerType:'',// static or interactive
        urlType:'',
        appUrl:'',
        linkUrl:'',
        isSlider: false,
        position:'',// 1,2,3,4,5
        startDate:'',
        expireDate:'',
        startTime:'',
        endTime:'',
        latitude:'',
        longitude:'',
        areaInKm:'',
    }
    const [bannerData,setBannerData] = useState(initialState);
    const BannerFormMethod = useForm({
        mode:'onChange',
        defaultValues:bannerData
    });
    const {control,watch,handleSubmit,setValue,formState:{isDirty,errors,dirtyFields },reset}= BannerFormMethod;
    useEffect(()=>{
        if(id>0){
            setIsLoading(true);
            // fetch banner data by id
            const fetchBannerData = async()=>{
                try {
                    const response = await BannerServices.getBannerById(id);
                    console.log('response:',response);
                    if(response.success == '1'){
                        const requiredData = {
                            title: response.data.title,
                            imageUrl:response.data.imageUrl,
                            platformName:response.data.platformName,
                            bannerCategory:response.data.bannerCategory,
                            bannerPosition:response.data.bannerPosition,
                            width: response.data.dimension ? response.data.dimension.split('x')[0] : '',
                            height: response.data.dimension? response.data.dimension.split('x')[1] : '',
                            aspectRatio: response.data.dimension ? calculateAspectRatio(response.data.dimension.split('x')[0], response.data.dimension.split('x')[1]) : '',
                            bannerType:response.data.bannerType,
                            urlType:response.data.bannerType == 'interactive' ? response.data.appUrl ? 'appUrl' : 'linkUrl' : '',
                            appUrl:response.data.appUrl,
                            linkUrl:response.data.linkUrl,
                            position:response.data.position == 0 ? '' : response.data.position,
                            startDate:dayjs(response.data.startDate).format('YYYY-MM-DD'),
                            expireDate:dayjs(response.data.expireDate).format('YYYY-MM-DD'),
                            startTime:dayjs(response.data.startDate).format('HH:mm'),
                            endTime:dayjs(response.data.expireDate).format('HH:mm'),
                            latitude:response.data.latitude,
                            longitude:response.data.longitude,
                            areaInKm:response.data.areaInKm == 0 ? '' : response.data.areaInKm,
                            isSlider:response.data.isSlider
                        }

                        setBannerData(response.data);
                        reset(requiredData,{isDirty:false});
                    }
                } catch (error) {
                    console.log("error",error);
                }finally{
                    setIsLoading(false);
                }
            }
            
            fetchBannerData();
        }
        fetchBannerCategories();
    },[])

    const section = watch('section');
    const type = watch('bannerType');
    const aspectRatio = watch('aspectRatio');
    
    const width = watch('width');
    const height = watch('height');
    const urlType = watch('urlType');

    useEffect(() => {
        if(type!='interactive'){
            setValue('urlType','');
            setValue('appUrl','');
            setValue('linkUrl','');
        }
    }, [type]);
    
    const [lastChanged, setLastChanged] = useState(null);
    useEffect(() => {
        if (aspectRatio && (width || height)) {
            const [w, h] = aspectRatio.split(':').map(Number);
            const ratio = w / h;
    
            // If editing, check if values are different from original
            const isEditing = id > 0;
            const originalWidth = bannerData?.dimension?.split('x')[0];
            const originalHeight = bannerData?.dimension?.split('x')[1];
    
            // If we have a width, calculate the new height
            if (width) {
                const calculatedHeight = Math.round(width / ratio);
                setValue('height', calculatedHeight, { 
                    // Only mark as dirty if editing and value is different from original
                    shouldDirty: isEditing ? 
                        (calculatedHeight.toString() !== originalHeight) : true,
                    shouldValidate: true 
                });
            }
            // If we don't have a width but have a height, calculate the width
            else if (height) {
                const calculatedWidth = Math.round(height * ratio);
                setValue('width', calculatedWidth, { 
                    // Only mark as dirty if editing and value is different from original
                    shouldDirty: isEditing ? 
                        (calculatedWidth.toString() !== originalWidth) : true,
                    shouldValidate: true 
                });
            }
        }
    }, [aspectRatio]);
    
    // Modify the effect for handling width/height changes
    useEffect(() => {
        if (aspectRatio && (width || height)) {
            const [w, h] = aspectRatio.split(':').map(Number);
            const ratio = w / h;
    
            // Check if we're editing
            const isEditing = id > 0;
            const originalWidth = bannerData?.dimension?.split('x')[0];
            const originalHeight = bannerData?.dimension?.split('x')[1];
    
            if (lastChanged === 'width' && width) {
                const calculatedHeight = Math.round(width / ratio);
                setValue('height', calculatedHeight, { 
                    shouldDirty: isEditing ? 
                        (calculatedHeight.toString() !== originalHeight) : true,
                    shouldValidate: true 
                });
            } else if (lastChanged === 'height' && height) {
                const calculatedWidth = Math.round(height * ratio);
                setValue('width', calculatedWidth, { 
                    shouldDirty: isEditing ? 
                        (calculatedWidth.toString() !== originalWidth) : true,
                    shouldValidate: true 
                });
            }
        }
    }, [width, height, aspectRatio, lastChanged]);
    
    // Add effect to handle aspect ratio changes in edit mode
    useEffect(() => {
        if (id > 0 && aspectRatio) {
            const originalWidth = bannerData?.dimension?.split('x')[0];
            const originalHeight = bannerData?.dimension?.split('x')[1];
            const originalAspectRatio = calculateAspectRatio(originalWidth, originalHeight);
            
            // If aspect ratio is different from original, mark as dirty
            if (aspectRatio !== originalAspectRatio) {
                setValue('aspectRatio', aspectRatio, { 
                    shouldDirty: true,
                    shouldValidate: true 
                });
            }
        }
    }, [aspectRatio, id, bannerData]);
      

    /*================== image upload realted ====================== */
    const [imageUploadResponseDetails,setImageUploadResponseDetails] = useState({});
    
    const [isLoading,setIsLoading] = useState (false);
    const [isSubmitting, setIsSubmitting] = useState(false);        
    

    // final form submit
    const onSubmit = async (data) => {
        setIsSubmitting(true);
        console.log(data);

        const modifiedData = {
            title: data.title,
            platformName:data.platformName,
            bannerCategory:data.bannerCategory,
            bannerPosition:data.bannerPosition,
            dimension:`${data.width}x${data.height}`,
            bannerType:data.bannerType,
            appUrl:urlType=='appUrl' ? data.appUrl : "",
            linkUrl:urlType=='linkUrl' ? data.linkUrl : "",   
            startDate:combineDateAndTime(data.startDate, data.startTime),
            expireDate:combineDateAndTime(data.expireDate, data.endTime), 
            latitude:data.latitude,
            longitude:data.longitude,
            areaInKm:data.areaInKm? data.areaInKm : 0,
            position:data.position ? data.position : 0,      
            imageUrl:data.imageUrl,
            isSlider:data.isSlider,
        }
        console.log("modifiedData",modifiedData);
        
        try {
            if(id>0){
                const updateResponse = await BannerServices.updateBanner(id,modifiedData);
                if (updateResponse.success == '1') {
                    const result = await Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: 'Banner updated successfully',
                    });
                    navigate(`/marketing/banner/view/${id}`);
                } else if(updateResponse.success == '0'){
                    Swal.fire({
                        icon:'error',
                        title: 'Failed',
                        text : updateResponse.message ||'Banner Data upadte failed',
                        // timer:1500
                    })
                }
            }else{
                // add new banner
                const createResponse = await BannerServices.addBanner(modifiedData);
                if (createResponse.success == '1') {
                    const result = await Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: 'Banner Created successfully',
                        // add new or show banner list button
                        showCancelButton: true,
                        confirmButtonText: 'Add New',
                        cancelButtonText: 'Show List',
                        confirmButtonColor: 'green',
                        cancelButtonColor: '#5856d6',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    });
                    if (result.isConfirmed) {
                        reset(initialState);
                        setBannerData(initialState);    
                        setImageUploadResponseDetails({});                    
                    }
                    if (result.dismiss === Swal.DismissReason.cancel) {
                        navigate('/marketing/banners');
                    }
                }else if(createResponse.success == '0'){
                    Swal.fire({
                        icon:'error',
                        title: 'Failed',
                        text : createResponse.message ||'Banner Addition Failed',
                        // timer:1500
                    })
                }
            }
        } catch (error) {
            console.log("error",error);
            Swal.fire({
                icon:'error',
                title: 'Failed',
                text : id>0 ? 'Banner Data upadte failed' : 'Banner Addition Failed',
                timer:1500
            })
        }finally{
            setIsSubmitting(false);
        }
        
    }

    // Helper function to combine date and time strings into a Date object
    const combineDateAndTime = (date, time) => {
        if (!date || !time) return null;
        const [hours, minutes] = time.split(':');
        const combined = new Date(date);
        combined.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0);
        return combined;
    };

    /*********** Add/Edit Banner Section Related ************/
    const [bannerCategoryList, setBannerCategoryList] = useState([]);
    const fetchBannerCategories = async() => {
        // fetch banner sectionList
        const response = await BannerServices.getBannerCategoryList();
        if (response.success == '1') {
            setBannerCategoryList(response.data);
        }
        // setBannerCategoryList(['Homescreen_Caraousel','Homescreen_static','Category_Caraousel','Category_static','Product_Caraousel','Product_static','Profile_Caraousel','Profile_static']);
    }
    
  return (
    <BannerDataContext.Provider value={{bannerData,setBannerData,imageUploadResponseDetails,setImageUploadResponseDetails,isSubmitting,combineDateAndTime,setLastChanged,fetchBannerCategories,bannerCategoryList,onSubmit}}>
        {(isLoading || isSubmitting) && <Loading/>}
        <PageHeaderLayout headerTitle={id>0 ? `Edit Banner Detail's`:'Add New Banner'}>
          <Button variant='contained' onClick={()=>navigate(-1)} sx={{ height: "30px", marginTop: "10px" }} startIcon={<ArrowBack />}>
            Back
          </Button>
        </PageHeaderLayout>
        <FormProvider {...BannerFormMethod}>
            <Box component={'form'} >
                <Grid container spacing={2}>
                    <BannerForm/>
                    {/* ==========  Image Section  ========= */}
                    <ImageSection/>
                </Grid>
            </Box>
        </FormProvider>
    </BannerDataContext.Provider>
  );
}

export default AddEditBanner;
export {BannerDataContext};
const formatValue = (value) => {
    if (typeof value !== 'string') {
      return value === 0 ? '0' : value?.toString() || '';
    }
    const formatted = value.replace(/^0+(?=\d)/, '').replace(/^\./, '0.');
    return formatted === '' ? '' : formatted;
};